import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { DatabaseService } from './DatabaseService';
import { Question, GameSession, Player, ScoreData, GrenadeThrowEvent } from '@/types';
import logger from '@/utils/logger';

export class FirebaseService implements DatabaseService {
  // Question management
  async getQuestions(): Promise<Question[]> {
    const questionsRef = collection(db, 'questions');
    const snapshot = await getDocs(questionsRef);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Question[];
  }

  async addQuestion(question: Omit<Question, 'id'>): Promise<string> {
    const questionsRef = collection(db, 'questions');
    const docRef = await addDoc(questionsRef, question);
    return docRef.id;
  }

  async updateQuestion(id: string, question: Partial<Question>): Promise<void> {
    const questionDoc = doc(db, 'questions', id);
    await updateDoc(questionDoc, question);
  }

  async deleteQuestion(id: string): Promise<void> {
    const questionDoc = doc(db, 'questions', id);
    await deleteDoc(questionDoc);
  }

  // Game session management
  async createGameSession(teacherId: string, questions: Question[]): Promise<GameSession> {
    const sessionData: Omit<GameSession, 'id'> = {
      gameCode: Math.random().toString(36).substring(2, 8).toUpperCase(),
      teacherId,
      teacherName: 'Teacher',
      questionSetId: 'default',
      questionSetName: 'Default Set',
      maxPlayers: 30,
      allowLateJoin: true,
      autoAdvance: false,
      players: [],
      currentQuestionIndex: 0,
      currentStageIndex: 0,
      stages: [],
      questions,
      isActive: true,
      gameState: 'lobby',
      status: 'waiting',
      createdAt: new Date(),
      updatedAt: new Date(),
      currentPhaseStartTime: null,
      currentPhaseEndTime: null,
      currentPhaseDurationSeconds: null,
      currentAnswerStats: null,
    };

    const sessionsRef = collection(db, 'gameSessions');
    const docRef = await addDoc(sessionsRef, {
      ...sessionData,
      createdAt: Timestamp.fromDate(sessionData.createdAt),
    });

    return {
      id: docRef.id,
      ...sessionData,
    };
  }

  async getGameSession(sessionId: string): Promise<GameSession | null> {
    const sessionDoc = doc(db, 'gameSessions', sessionId);
    const snapshot = await getDoc(sessionDoc);

    if (!snapshot.exists()) return null;

    const data = snapshot.data();
    return {
      id: snapshot.id,
      ...data,
      createdAt: data.createdAt.toDate(),
    } as GameSession;
  }

  async updateGameSession(sessionId: string, updates: Partial<GameSession>): Promise<void> {
    const sessionDoc = doc(db, 'gameSessions', sessionId);
    await updateDoc(sessionDoc, updates);
  }

  async endGameSession(sessionId: string): Promise<void> {
    const sessionDoc = doc(db, 'gameSessions', sessionId);
    await updateDoc(sessionDoc, {
      isActive: false,
      gameState: 'finished',
    });
  }

  // Player management
  async addPlayerToSession(sessionId: string, player: Player): Promise<void> {
    const session = await this.getGameSession(sessionId);
    if (!session) throw new Error('Game session not found');

    const updatedPlayers = [...session.players, player];
    await this.updateGameSession(sessionId, { players: updatedPlayers });
  }

  async removePlayerFromSession(sessionId: string, playerId: string): Promise<void> {
    const session = await this.getGameSession(sessionId);
    if (!session) throw new Error('Game session not found');

    const updatedPlayers = session.players.filter(p => p.id !== playerId);
    await this.updateGameSession(sessionId, { players: updatedPlayers });
  }

  async updatePlayerPosition(sessionId: string, playerId: string, position: { x: number; y: number }): Promise<void> {
    const session = await this.getGameSession(sessionId);
    if (!session) throw new Error('Game session not found');

    const updatedPlayers = session.players.map(player =>
      player.id === playerId ? { ...player, position } : player
    );
    await this.updateGameSession(sessionId, { players: updatedPlayers });
  }

  async updatePlayerItems(sessionId: string, playerId: string, updates: {
    netCharges?: number;
    grenadeCharges?: number;
    turboAvailable?: boolean;
    turboUsed?: boolean;
    shoesEffectNextRound?: boolean;
    lastGrenadeThrow?: import('@/types').GrenadeThrowEvent | null;
    position?: { x: number; y: number };
  }): Promise<void> {
    const session = await this.getGameSession(sessionId);
    if (!session) throw new Error('Game session not found');

    const updatedPlayers = session.players.map(player =>
      player.id === playerId ? { ...player, ...updates } : player
    );
    await this.updateGameSession(sessionId, { players: updatedPlayers });
  }

  async updatePlayerScore(sessionId: string, playerId: string, score: number, roundScores?: number[], currentRoundScore?: number): Promise<void> {
    const session = await this.getGameSession(sessionId);
    if (!session) throw new Error('Game session not found');

    const updatedPlayers = session.players.map(player => {
      if (player.id === playerId) {
        const updates: Player = { ...player, score };
        if (roundScores !== undefined) {
          updates.roundScores = roundScores;
        }
        if (currentRoundScore !== undefined) {
          updates.currentRoundScore = currentRoundScore;
        }
        return updates;
      }
      return player;
    });
    await this.updateGameSession(sessionId, { players: updatedPlayers });
  }

  // Scoring
  async saveScore(scoreData: ScoreData): Promise<void> {
    const scoresRef = collection(db, 'scores');
    await addDoc(scoresRef, scoreData);
  }

  async getSessionScores(sessionId: string): Promise<ScoreData[]> {
    const scoresRef = collection(db, 'scores');
    const q = query(scoresRef, where('sessionId', '==', sessionId));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => doc.data()) as ScoreData[];
  }

  async getPlayerStats(playerId: string): Promise<{
    totalGames: number;
    averageScore: number;
    bestScore: number;
  }> {
    const scoresRef = collection(db, 'scores');
    const q = query(
      scoresRef,
      where('playerId', '==', playerId),
      orderBy('pointsEarned', 'desc')
    );
    const snapshot = await getDocs(q);

    const scores = snapshot.docs.map(doc => doc.data() as ScoreData);
    const totalGames = scores.length;
    const averageScore = scores.reduce((sum, score) => sum + score.pointsEarned, 0) / totalGames || 0;
    const bestScore = scores.length > 0 ? scores[0].pointsEarned : 0;

    return { totalGames, averageScore, bestScore };
  }

  // Real-time subscriptions
  subscribeToGameSession(sessionId: string, callback: (session: GameSession) => void): () => void {
    const sessionDoc = doc(db, 'gameSessions', sessionId);
    return onSnapshot(sessionDoc, (doc) => {
      if (doc.exists()) {
        const data = doc.data();

        // Helper to convert Firestore Timestamps to milliseconds
        const convertTimestampToMs = (timestamp: Timestamp | Date | number | { seconds: number } | undefined | null): number => {
          if (typeof timestamp === 'number') return timestamp;
          if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp && typeof timestamp.toDate === 'function') {
            return timestamp.toDate().getTime();
          }
          if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
            return timestamp.seconds * 1000;
          }
          return timestamp instanceof Date ? timestamp.getTime() : Date.now();
        };

        // Normalize net projectiles from Firestore
        const normalizeNetProjectiles = (nets: unknown[]): import('@/types').NetProjectile[] => {
          if (!Array.isArray(nets)) {
            return [];
          }

          return nets.map(net => {
            try {
              const netObj = net as Record<string, unknown>;
              const startPos = netObj.startPosition as { x?: unknown; y?: unknown } | undefined;
              const currentPos = netObj.currentPosition as { x?: unknown; y?: unknown } | undefined;
              const targetPos = netObj.targetPosition as { x?: unknown; y?: unknown } | undefined;

              const normalized: import('@/types').NetProjectile = {
                id: String(netObj.id || ''),
                thrownBy: String(netObj.thrownBy || ''),
                startPosition: { x: Number(startPos?.x || 0), y: Number(startPos?.y || 0) },
                currentPosition: { x: Number(currentPos?.x || 0), y: Number(currentPos?.y || 0) },
                targetPosition: { x: Number(targetPos?.x || 0), y: Number(targetPos?.y || 0) },
                createdAt: convertTimestampToMs(netObj.createdAt as Timestamp | Date | number | { seconds: number } | undefined | null),
                isActive: Boolean(netObj.isActive),
                ...(netObj.deactivatedAt
                  ? { deactivatedAt: convertTimestampToMs(netObj.deactivatedAt as Timestamp | Date | number | { seconds: number } | undefined | null) }
                  : {})
              };
              return normalized;
            } catch (err) {
              console.error('❌ FirebaseService: Failed to normalize net:', net, err);
              return null;
            }
          }).filter((net): net is import('@/types').NetProjectile =>
            net !== null &&
            net.id !== '' &&
            !isNaN(net.startPosition.x) &&
            !isNaN(net.createdAt)
          );
        };

        // Normalize grenade projectiles
        const normalizeGrenadeProjectiles = (grenades: unknown[]): import('@/types').GrenadeProjectile[] => {
          if (!Array.isArray(grenades)) {
            return [];
          }

          return grenades.map(grenade => {
            try {
              const grenadeObj = grenade as Record<string, unknown>;
              const startPos = grenadeObj.startPosition as { x?: unknown; y?: unknown } | undefined;
              const currentPos = grenadeObj.currentPosition as { x?: unknown; y?: unknown } | undefined;
              const velocity = grenadeObj.velocity as { x?: unknown; y?: unknown } | undefined;
              const explosionPos = grenadeObj.explosionPosition as { x?: unknown; y?: unknown } | undefined;

              const normalized: import('@/types').GrenadeProjectile = {
                id: String(grenadeObj.id || ''),
                thrownBy: String(grenadeObj.thrownBy || ''),
                startPosition: { x: Number(startPos?.x || 0), y: Number(startPos?.y || 0) },
                currentPosition: { x: Number(currentPos?.x || 0), y: Number(currentPos?.y || 0) },
                velocity: { x: Number(velocity?.x || 0), y: Number(velocity?.y || 0) },
                chargeAmount: Number(grenadeObj.chargeAmount || 0),
                throwDistance: Number(grenadeObj.throwDistance || 240),
                createdAt: convertTimestampToMs(grenadeObj.createdAt as Timestamp | Date | number | { seconds: number } | undefined | null),
                isActive: Boolean(grenadeObj.isActive),
                hasExploded: Boolean(grenadeObj.hasExploded),
                bouncesRemaining: Number(grenadeObj.bouncesRemaining || 0),
                ...(explosionPos && {
                  explosionPosition: {
                    x: Number(explosionPos.x || 0),
                    y: Number(explosionPos.y || 0)
                  }
                }),
                ...(grenadeObj.deactivatedAt
                  ? { deactivatedAt: convertTimestampToMs(grenadeObj.deactivatedAt as Timestamp | Date | number | { seconds: number } | undefined | null) }
                  : {})
              };
              return normalized;
            } catch (err) {
              console.error('❌ FirebaseService: Failed to normalize grenade:', grenade, err);
              return null;
            }
          }).filter((grenade): grenade is import('@/types').GrenadeProjectile =>
            grenade !== null &&
            grenade.id !== '' &&
            !isNaN(grenade.startPosition.x) &&
            !isNaN(grenade.createdAt)
          );
        };

        // Normalize players array (immobilization and push effect timestamps)
        const normalizePlayers = (players: unknown[]): Player[] => {
          if (!Array.isArray(players)) return [];

          const normalizeGrenadeThrowEvent = (event: unknown): GrenadeThrowEvent | undefined => {
            if (!event || typeof event !== 'object') {
              return undefined;
            }

            const eventObj = event as Record<string, unknown>;
            const id = typeof eventObj.id === 'string'
              ? eventObj.id
              : eventObj.id != null
                ? String(eventObj.id)
                : '';

            if (!id) {
              return undefined;
            }

            const startPos = eventObj.startPosition as { x?: unknown; y?: unknown } | undefined;
            const vel = eventObj.velocity as { x?: unknown; y?: unknown } | undefined;

            return {
              id,
              thrownAt: Number(eventObj.thrownAt ?? eventObj.createdAt ?? Date.now()),
              startPosition: {
                x: Number(startPos?.x || 0),
                y: Number(startPos?.y || 0)
              },
              velocity: {
                x: Number(vel?.x || 0),
                y: Number(vel?.y || 0)
              },
              chargeAmount: Number(eventObj.chargeAmount ?? 0),
              throwDistance: Number(eventObj.throwDistance ?? 0),
              bouncesRemaining: Number(eventObj.bouncesRemaining ?? 0)
            };
          };

          return players.map(player => {
            const playerObj = player as Player;
            const normalizedEvent = normalizeGrenadeThrowEvent(playerObj.lastGrenadeThrow);

            return {
              ...playerObj,
              ...(normalizedEvent ? { lastGrenadeThrow: normalizedEvent } : {}),
              immobilizedUntil: playerObj.immobilizedUntil
                ? convertTimestampToMs(playerObj.immobilizedUntil as Timestamp | Date | number | { seconds: number } | undefined | null)
                : undefined,
              pushEndTime: playerObj.pushEndTime
                ? convertTimestampToMs(playerObj.pushEndTime as Timestamp | Date | number | { seconds: number } | undefined | null)
                : undefined
            };
          });
        };

        // Helper to convert Firestore Timestamps to Date objects
        const convertTimestampToDate = (timestamp: Timestamp | Date | { seconds: number } | undefined | null): Date | undefined => {
          if (!timestamp) return undefined;
          if (typeof timestamp === 'object' && 'toDate' in timestamp && typeof timestamp.toDate === 'function') {
            return timestamp.toDate();
          }
          if (typeof timestamp === 'object' && 'seconds' in timestamp) {
            return new Date(timestamp.seconds * 1000);
          }
          return timestamp instanceof Date ? timestamp : undefined;
        };

        // Overlay playerPositions map onto normalized players for contention-free position sync
        const normalizedPlayers = normalizePlayers(data.players || []);
        const playerPositions = data.playerPositions as Record<string, { x: number; y: number }> | undefined;
        if (playerPositions && typeof playerPositions === 'object') {
          for (const player of normalizedPlayers) {
            const pos = playerPositions[player.id];
            if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
              player.position = { x: pos.x, y: pos.y };
            }
          }
        }

        const session = {
          id: doc.id,
          ...data,
          players: normalizedPlayers,
          activeNetProjectiles: normalizeNetProjectiles(data.activeNetProjectiles || []),
          activeGrenadeProjectiles: normalizeGrenadeProjectiles(data.activeGrenadeProjectiles || []),
          createdAt: data.createdAt.toDate(),
          // Convert phase timing timestamps from Firestore to Date objects
          currentPhaseStartTime: convertTimestampToDate(data.currentPhaseStartTime),
          currentPhaseEndTime: convertTimestampToDate(data.currentPhaseEndTime),
          updatedAt: convertTimestampToDate(data.updatedAt),
          startedAt: convertTimestampToDate(data.startedAt),
          completedAt: convertTimestampToDate(data.completedAt),
        } as GameSession;

        logger.network('Firebase session update received', {
          sessionId: session.id,
          playerCount: session.players.length,
          netCount: session.activeNetProjectiles?.length || 0,
          grenadeCount: session.activeGrenadeProjectiles?.length || 0
        });
        callback(session);
      }
    });
  }

  subscribeToPlayerPositions(sessionId: string, callback: (players: Player[]) => void): () => void {
    const sessionDoc = doc(db, 'gameSessions', sessionId);
    return onSnapshot(sessionDoc, (doc) => {
      if (doc.exists()) {
        const session = doc.data() as GameSession;
        callback(session.players);
      }
    });
  }
}
